import { DataPointsApiResponse } from '@tableau/metrics-engine';
import { Dictionary } from 'underscore';

import { Loadable, LoadingStatus } from '~src/httpClient/utils/Loadable';
import * as MixedContentHelpers from '~src/spacesPlatform/utils/MixedContentHelpers';

import { AppState } from '../bootstrap/redux/state/AppState';
import { ExplorableItemType } from '../spacesPlatform/models/ContentType';
import { IContentInfo } from '../spacesPlatform/models/IContentInfo';
import { isUsageMetricInfo } from './models/IMetricInfo';
import { LoadableDataPointsById,MetricsDataState } from './state/MetricsDataState';

export const getLoadableData = (state: LoadableDataPointsById, id: string): Loadable<DataPointsApiResponse> => {
  const model = state[id];

  return model || Loadable.notLoaded;
};

export const getMetricsDataState = (state: AppState): MetricsDataState => {
  return state.metricsData;
};

export const getMetricsDataPoints = (state: AppState): LoadableDataPointsById => {
  return state.metricsData.dataPointsById;
};

export function selectMetricDataForMetricId(state: AppState, id: string): DataPointsApiResponse | undefined {
  const loadableContent = getLoadableData(getMetricsDataPoints(state), id);
  if (loadableContent.loadingStatus === LoadingStatus.LOADED) {
    return loadableContent.content;
  }

  return undefined;
}

type RowContent = IContentInfo<ExplorableItemType> | Dictionary<any>;

export function selectMetricDataForContentItem(state: AppState, content: RowContent): DataPointsApiResponse | undefined {
  if (!content || content.contentType !== 'metric') {
    return undefined;
  }
  if (isUsageMetricInfo(content)) {
    // usage metrics have a separate API where the data points are already provided
    return content.dataPointsApiResponse;
  }

  return selectMetricDataForMetricId(state, content.id);
}

export const notLoadedData = (state: LoadableDataPointsById, ids: ReadonlyArray<string>): ReadonlyArray<string> => {
  return ids
    .map(id => ({ id, loadable: getLoadableData(state, id)}))
    .filter(item => item.loadable.loadingStatus === LoadingStatus.NOT_LOADED)
    .map(item => item.id);
};

export const getMetricRefreshList = (state: MetricsDataState): ReadonlyArray<string> => {
  return state.metricRefreshList.reduce((metricList, id) => {
    if (getLoadableData(state.dataPointsById, id).loadingStatus !== LoadingStatus.LOADING) {
      metricList.push(id);
    }
    return metricList;
  }, []);
};

export function userCanResumeSuspendedMetric(state: AppState, item: RowContent): boolean {
  const data = selectMetricDataForContentItem(state, item);
  if (!data) {
    return false;
  }
  return MixedContentHelpers.currentUserIsOwnerOrAdmin(state, item);
}

export function userIsMetricOwner(state: AppState, item: RowContent): boolean {
  const data = selectMetricDataForContentItem(state, item);
  if (!data) {
    return false;
  }
  return MixedContentHelpers.currentUserIsOwner(state, item);
}
